import { Box, Container, forwardRef, Link, Spinner, Text, VStack } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useAccount, useBalance } from "wagmi";

import { AssetDescription } from "./AssetDescription";
import { ERROR_INSUFFICIENT_BALANCE } from "./DcaErrors";
import { DcaConfigErrors, hasDcaErrors } from "../../components/dca/DcaErrors";
import { getSupportedToken, getTokenAddress } from "../../config/tokens";
import { calculateDailyAmount, calculateEscrowAmount } from "../../helpers/dca";
import { TokenUpgrader } from "../wallet/TokenUpgraderModal";
import { formatEther, parseEther } from "ethers/lib/utils";
import { convertToDecimals, formatToDisplay } from "../../helpers/numbers";

function calculateRequiredAmounts(erc20Data, tokenxData, length, amount) {
  const TOLERANCE_VAL = parseEther("1"); // NOTE: this means a tolerance of 1 USDCx or WMATICx
  let adjustedErc20Bal = convertToDecimals(erc20Data.value, tokenxData.decimals, erc20Data.decimals);
  let reqDailyAmount = calculateDailyAmount(length, amount);
  let missingTokenx, missingErc20;

  if (length > 0 && adjustedErc20Bal.gte(amount)) {
    // rolling length and user has enough funds, so calculate based on the total amount
    missingTokenx = amount.sub(tokenxData.value);
    missingErc20 = missingTokenx.sub(adjustedErc20Bal); // should be <= 0
  } else {
    // in this case, calculate based on the minimum required
    missingTokenx = reqDailyAmount.mul(10).sub(tokenxData.value); // require minimum 10 days worth of tokens
    missingErc20 = missingTokenx.sub(adjustedErc20Bal);
  }

  let doesNotHaveEnoughTokenx = missingTokenx.gt(TOLERANCE_VAL);
  let doesNotHaveEnoughErc20 = missingErc20.gt(0);

  return {
    doesNotHaveEnoughErc20,
    doesNotHaveEnoughTokenx,
    missingErc20,
    missingTokenx,
  };
}

function BalancesAlert({ tokenxData, tokenxIsError, tokenxIsLoading, erc20Data, erc20IsError, erc20IsLoading }) {
  const containerBgColor = tokenxIsError || erc20IsError ? "#fed7d7" : "#bee3f8";

  return (
    <Container bg={containerBgColor} py="1em" borderRadius="0.25em">
      {tokenxIsError || erc20IsError ? (
        <Text>☠️ Something's broken! Couldn't fetch your balances! 😞</Text>
      ) : (
        <Text>
          ⚖️
          {tokenxIsLoading || erc20IsLoading ? (
            <Spinner size="xs" as="span" ml="0.3em" />
          ) : (
            " You have " +
            formatToDisplay(tokenxData.formatted) +
            " " +
            tokenxData.symbol +
            " and " +
            formatToDisplay(erc20Data.formatted) +
            " " +
            erc20Data.symbol +
            "."
          )}
        </Text>
      )}
    </Container>
  );
}

function DcaLengthInfo({ tokenxData, amount, length }) {
  return (
    <Container bg="#deffc4" py="1em" borderRadius="0.25em">
      {length === 0 ? (
        <Text>💸 BTFDCA will run FOREVER (or until your wallet is out of funds).</Text>
      ) : (
        <Text>
          💸 You will spend {formatToDisplay(formatEther(amount.div(length)))} {tokenxData.symbol} per day in the
          ROLLING plan.
        </Text>
      )}
      <Text fontSize="sm">Make sure you keep your wallet topped up.</Text>
    </Container>
  );
}

function RequiredBalanceAlert({
  erc20Data,
  tokenxData,
  length,
  amount,
  escrowAmount,
  chainId,
  account,
  dcaConfigErrors,
}) {
  // format the data - TODO: rethink how this object is used
  let tokenBalance = {
    // erc20Balance: erc20Data.value,
    // tokenxBalance: tokenxData.value,
    erc20Symbol: erc20Data.symbol,
    tokenxSymbol: tokenxData.symbol,
    erc20Address: getTokenAddress(chainId, erc20Data.symbol),
    tokenxAddress: getTokenAddress(chainId, tokenxData.symbol),
  };

  // check and calculate missing amounts
  let { doesNotHaveEnoughErc20, doesNotHaveEnoughTokenx, missingErc20, missingTokenx } = calculateRequiredAmounts(
    erc20Data,
    tokenxData,
    length,
    amount
  );

  return (
    <>
      {doesNotHaveEnoughErc20 ? (
        <Container bg="#FED7D7" borderRadius="0.25em" py="1em">
          <Text fontWeight="bold">❌ NGMI.</Text>
          <Box>
            You do not have enough funds ({erc20Data.symbol}+{tokenxData.symbol}) to BTFDCA! ☠️
          </Box>
          <Box fontSize="sm">We require 10 days worth of funds in your wallet to start a stream.</Box>
          <Box fontSize="sm">
            You are short {formatToDisplay(formatEther(missingErc20))} {erc20Data.symbol}.
          </Box>
        </Container>
      ) : (
        doesNotHaveEnoughTokenx && (
          <Container bg="#FEEBC8" borderRadius="0.25em" py="1em">
            <Text fontWeight="bold">💸 Get some more {tokenxData.symbol}</Text>
            <Box>
              <Text py="0.25em">
                <TokenUpgrader
                  btnLabel="Upgrade"
                  colorScheme="orange"
                  account={account}
                  tokenBalance={tokenBalance}
                  defaultAmount={
                    erc20Data.decimals !== tokenxData.decimals
                      ? convertToDecimals(missingTokenx, erc20Data.decimals, tokenxData.decimals)
                      : missingTokenx
                  }
                />
                &nbsp; at least {formatToDisplay(formatEther(missingTokenx))} {erc20Data.symbol} to {tokenxData.symbol}.
              </Text>
            </Box>
            <Link href="/faq#erc20-tokenx" fontSize="xs" isExternal mt="0.5em" display="block">
              Click here to learn more about ERC20 and SuperTokens.
            </Link>
          </Container>
        )
      )}

      {!hasDcaErrors(dcaConfigErrors) && (
        <>
          <Container bg="#C6F6D5" borderRadius="0.25em" py="1em">
            "✔️ All good!, LFG! 🔥🔥🔥"
          </Container>

          <Container bg="#bee3f8" borderRadius="0.25em" py="1em">
            <Text>
              🔐 NOTE: {formatToDisplay(formatEther(escrowAmount))} {tokenxData.symbol} will be locked in advance for
              escrow.
            </Text>
            <Link href="/faq#escrow" fontSize="xs" isExternal display="block">
              Click here to learn more about how the escrow works.
            </Link>
          </Container>
        </>
      )}
    </>
  );
}

const DcaInfo = forwardRef(
  ({ chainId, sourceToken, targetToken, length, amount, setDcaConfigErrors, dcaConfigErrors, ...props }, ref) => {
    // function DcaInfo({ chainId, sourceToken, targetToken, length, amount, setDcaConfigErrors, dcaConfigErrors }) {
    const { address } = useAccount();
    const [escrowAmount, setEscrowAmount] = useState();
    const [showDcaErrors, setShowDcaErrors] = useState(false);

    // hooks for getting the balance for source token and source tokenx
    const {
      data: tokenxData,
      isError: tokenxIsError,
      isLoading: tokenxIsLoading,
    } = useBalance({
      addressOrName: address,
      token: getSupportedToken(chainId, sourceToken)?.tokenxAddress,
      watch: true,
    });
    const {
      data: erc20Data,
      isError: erc20IsError,
      isLoading: erc20IsLoading,
    } = useBalance({
      addressOrName: address,
      token: getSupportedToken(chainId, sourceToken)?.erc20Address,
      watch: true,
    });

    // hook for calculating required escrow amount
    useEffect(() => {
      if (sourceToken && targetToken) setEscrowAmount(calculateEscrowAmount(length, amount));
      else setEscrowAmount(null);
    }, [sourceToken, targetToken, length, amount]);

    // hook for error detection
    useEffect(() => {
      // BUG: Maximum update depth exceeded.
      if (escrowAmount && erc20Data && tokenxData) {
        // ATTN: this MUST be consistent with the calculation in RequiredBalanceAlert
        // we only use this flag to set the error (to block the LFG button);
        // RequiredBalanceAlert performs the equivalent calculations for display purposes
        let { doesNotHaveEnoughTokenx } = calculateRequiredAmounts(erc20Data, tokenxData, length, amount);

        // adds an error to DcaConfigErrors to block the LFG button
        // it does not have any other visual impact
        setDcaConfigErrors((prevErrs) => {
          let errs = { ...prevErrs };

          if (doesNotHaveEnoughTokenx) errs[ERROR_INSUFFICIENT_BALANCE] = ERROR_INSUFFICIENT_BALANCE;
          else if (ERROR_INSUFFICIENT_BALANCE in errs) delete errs[ERROR_INSUFFICIENT_BALANCE];

          return errs;
        });
      }
    }, [length, amount, escrowAmount, tokenxData, erc20Data, setDcaConfigErrors]);

    // hook for showing/hiding errors
    useEffect(() => {
      if (sourceToken && targetToken && hasDcaErrors(dcaConfigErrors)) setShowDcaErrors(true);
      else setShowDcaErrors(false);
    }, [sourceToken, targetToken, dcaConfigErrors]);

    return (
      <VStack textColor="black" {...props} ref={ref}>
        <AssetDescription chainId={chainId} token={targetToken} />

        {sourceToken && (
          <BalancesAlert
            tokenxData={tokenxData}
            tokenxIsError={tokenxIsError}
            tokenxIsLoading={tokenxIsLoading}
            erc20Data={erc20Data}
            erc20IsError={erc20IsError}
            erc20IsLoading={erc20IsLoading}
          />
        )}

        {sourceToken && targetToken && (
          <>
            <DcaLengthInfo tokenxData={tokenxData} length={length} amount={amount} />

            {escrowAmount && erc20Data && tokenxData && (
              <RequiredBalanceAlert
                chainId={chainId}
                account={address}
                erc20Data={erc20Data}
                tokenxData={tokenxData}
                length={length}
                amount={amount}
                escrowAmount={escrowAmount}
                dcaConfigErrors={dcaConfigErrors}
              />
            )}
          </>
        )}

        {showDcaErrors && <DcaConfigErrors dcaConfigErrors={dcaConfigErrors} />}
      </VStack>
    );
  }
);

export default DcaInfo;
